import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';

const ACTIVITY_LEVELS = ['Sedentary', 'Lightly Active', 'Moderately Active', 'Very Active', 'Extremely Active'];
const GOALS = ['Lose Weight', 'Maintain Weight', 'Gain Muscle', 'Improve Health', 'Increase Endurance'];

export default function Signup() {
  const { signup, createUserProfile } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    displayName: '', email: '', password: '',
    age: '', gender: 'prefer not to say', height: '', currentWeight: '',
    goalWeight: '', activityLevel: 'Moderately Active', primaryGoal: 'Lose Weight',
  });

  const up = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const cred = await signup(form.email, form.password, form.displayName);
      await createUserProfile(cred.user.uid, {
        displayName: form.displayName,
        email: form.email,
        age: Number(form.age),
        gender: form.gender,
        height: Number(form.height),
        currentWeight: Number(form.currentWeight),
        goals: {
          targetWeight: Number(form.goalWeight),
          activityLevel: form.activityLevel,
          primaryGoal: form.primaryGoal,
        },
      });
      toast.success('Welcome to VitalForge!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: theme.gradient, padding: '1rem',
    }}>
      <div className="card animate-fade" style={{ width: '100%', maxWidth: '480px' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{
            fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', fontWeight: 700, letterSpacing: '2px',
            background: `linear-gradient(135deg, ${theme.accent}, ${theme.success})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>CREATE YOUR PROFILE</h1>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
            {[1,2].map(s => (
              <div key={s} style={{
                width: '40px', height: '4px', borderRadius: '2px',
                background: step >= s ? theme.accent : theme.border,
                transition: 'all 0.3s ease',
              }} />
            ))}
          </div>
        </div>

        {step === 1 && (
          <div className="animate-fade">
            <div className="form-group">
              <label>Full Name</label>
              <input value={form.displayName} onChange={e => up('displayName', e.target.value)} placeholder="John Doe" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email} onChange={e => up('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={form.password} onChange={e => up('password', e.target.value)} placeholder="Min 6 characters" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Age</label>
                <input type="number" value={form.age} onChange={e => up('age', e.target.value)} placeholder="25" />
              </div>
              <div className="form-group">
                <label>Gender</label>
                <select value={form.gender} onChange={e => up('gender', e.target.value)}>
                  <option>Male</option><option>Female</option><option>Non-binary</option><option>prefer not to say</option>
                </select>
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => setStep(2)} disabled={!form.displayName || !form.email || !form.password}>
              Continue →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Height (inches)</label>
                <input type="number" value={form.height} onChange={e => up('height', e.target.value)} placeholder='70"' />
              </div>
              <div className="form-group">
                <label>Current Weight (lbs)</label>
                <input type="number" value={form.currentWeight} onChange={e => up('currentWeight', e.target.value)} placeholder="200" />
              </div>
            </div>
            <div className="form-group">
              <label>Goal Weight (lbs)</label>
              <input type="number" value={form.goalWeight} onChange={e => up('goalWeight', e.target.value)} placeholder="175" />
            </div>
            <div className="form-group">
              <label>Activity Level</label>
              <select value={form.activityLevel} onChange={e => up('activityLevel', e.target.value)}>
                {ACTIVITY_LEVELS.map(l => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Primary Goal</label>
              <select value={form.primaryGoal} onChange={e => up('primaryGoal', e.target.value)}>
                {GOALS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || !form.currentWeight || !form.goalWeight}>
                {loading ? 'Creating...' : '⚡ Launch'}
              </button>
            </div>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: '1.5rem', color: theme.textSecondary, fontSize: '0.85rem' }}>
          Already have an account? <Link to="/login" style={{ color: theme.accent }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
